import { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button, useToastContext } from '@resideo/blueprint-oneportal';
import { useTranslation } from 'react-i18next';
import apiService from '@/services/apiService';
import type { ConsumerUser } from '@/services/apiService';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { PermissionKey } from '@/services/permissionService';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { ChangeRoleModal } from './ChangeRoleModal';
import { ManageDeviceAccessPage } from './ManageDeviceAccessPage';
import type { ManageDeviceAccessHandle } from './ManageDeviceAccessPage';
import { AddLocationPage } from './AddLocationPage';
import type { AddLocationHandle } from './AddLocationPage';
import { EmployeeDetailLocationsTab } from './EmployeeDetailLocationsTab';
import { StickyFooter } from './StickyFooter';
import styles from './EmployeeDetail.module.css';
import { getRoleLabel } from './roleUtils';

type DetailTab = 'info' | 'activity' | 'location';

function displayName(cu: ConsumerUser): string {
  const first = cu.user?.firstName?.trim() ?? '';
  const last = cu.user?.lastName?.trim() ?? '';
  const full = [first, last].filter(Boolean).join(' ');
  return full || cu.user?.contactEmail || '—';
}

export function EmployeeDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { selectedLocation, locations: contextLocations } = useAuth();
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const { addToast } = useToastContext();

  // Derive consumerAccountId from AuthContext — same pattern as Employees.tsx.
  // Avoids an extra getConsumerAccounts() API call on mount.
  const consumerAccountId: string | null =
    selectedLocation?.consumerAccountId ??
    contextLocations[0]?.consumerAccountId ??
    null;

  // If navigating from the Employees list, the user object is passed via router state
  const stateUser = (location.state as { user?: ConsumerUser } | null)?.user;

  const [user, setUser] = useState<ConsumerUser | null>(stateUser ?? null);
  const [isLoading, setIsLoading] = useState(!stateUser);
  const [error, setError] = useState<string | null>(null);

  interface ManageAccessState {
    locationId: string;
  }

  const [activeTab, setActiveTab] = useState<DetailTab>('info');
  const [manageAccessState, setManageAccessState] = useState<ManageAccessState | null>(null);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showChangeRoleModal, setShowChangeRoleModal] = useState(false);

  // ─ Refs + state for child page headers ─────────────────────────────────
  const manageRef = useRef<ManageDeviceAccessHandle>(null);
  const [isManageAccessSaving, setIsManageAccessSaving] = useState(false);
  const [manageAccessChangesCount, setManageAccessChangesCount] = useState(0);
  const addLocationRef = useRef<AddLocationHandle>(null);
  const [isAddLocationSaving, setIsAddLocationSaving] = useState(false);
  const [isAddLocationValid, setIsAddLocationValid] = useState(false);

  // ─── Effect: fetch user data ─────────────────────────────────────────────────
  // Skipped when user was passed via router state (navigating from the list).
  // Uses consumerAccountId derived from AuthContext — no extra accounts API call.

  useEffect(() => {
    if (stateUser) return; // already have the data — skip API calls

    let cancelled = false;

    async function load() {
      setIsLoading(true);
      setError(null);
      try {
        if (!consumerAccountId) {
          if (!cancelled) setError(t('employeeDetail.accountNotFound'));
          return;
        }

        const users = await apiService.getConsumerUsers(consumerAccountId);
        if (cancelled) return;

        const found = users.find((u: ConsumerUser) => u.id === id);
        if (!found) {
          setError(t('employeeDetail.employeeNotFound'));
          return;
        }

        setUser(found);
      } catch {
        if (!cancelled) setError(t('employeeDetail.unableToLoad'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [id, stateUser, consumerAccountId]);

  // ─── Manage Access (Locations tab) ───────────────────────────────────────────

  function handleManageAccess(locationId: string, _locationName: string) {
    setManageAccessState({ locationId });
  }

  // ─── Change Role ──────────────────────────────────────────────────────────────

  function handleRoleChanged(newRole: string) {
    setUser(prev => prev ? { ...prev, role: newRole } : prev);
    setShowChangeRoleModal(false);
    queryClient.invalidateQueries({
      queryKey: ['consumerUserDetail', consumerAccountId, id],
    });
  }

  // ─── Delete ──────────────────────────────────────────────────────────────────

  async function handleDelete() {
    if (!consumerAccountId || !user) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await apiService.deleteConsumerUser(consumerAccountId, user.id);
      addToast({ message: t('employeeDetail.userDeleted'), toastType: 'Success', zIndex: '9999' });
      navigate('/employees');
    } catch {
      setDeleteError(t('employeeDetail.failedToArchive'));
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  }

  // ─── Loading ─────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.stateContainer}>{t('employeeDetail.loading')}</div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className={styles.container}>
        <div className={styles.stateContainer}>
          <span className={styles.errorText}>{error ?? t('employeeDetail.employeeNotFound')}</span>
        </div>
      </div>
    );
  }

  const email = user.user?.contactEmail ?? '—';

  // A user is active if they were never invited (account owner/creator)
  // or they have at least one ACCEPTED invite. Role is irrelevant.
  const isActive = (user.invites?.length ?? 0) === 0 || (user.invites?.some(i => i.status === 'ACCEPTED') ?? false);

  // Add Location — uses EmployeeDetail's own header so height is consistent
  if (showAddLocation && consumerAccountId && id) {
    return (
      <div className={styles.container}>
        <div className={`${styles.header} ${styles.headerSeparator}`}>
          <button className={styles.backButton} onClick={() => { if (!isAddLocationSaving) setShowAddLocation(false); }} title={t('employeeDetail.cancel')}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <h1 className={styles.pageTitle}>{t('employeeDetail.addLocationsTitle', { name: displayName(user) })}</h1>
          <div className={styles.headerActions} />
        </div>
        <AddLocationPage
          ref={addLocationRef}
          consumerAccountId={consumerAccountId}
          consumerUserId={id}
          email={email}
          role={user.role}
          invitedUserId={user.user?.id ?? null}
          onSavingChange={setIsAddLocationSaving}
          onValidChange={setIsAddLocationValid}
          onClose={() => setShowAddLocation(false)}
        />
        <StickyFooter
          changesCount={0}
          onCancel={() => setShowAddLocation(false)}
          onSave={() => addLocationRef.current?.save()}
          isSaving={isAddLocationSaving}
          saveDisabled={!isAddLocationValid}
        />
      </div>
    );
  }

  // Manage Access — uses EmployeeDetail's own header so height is consistent
  if (manageAccessState && consumerAccountId && id) {
    const isViewOnly = user.role === 'ADMIN';
    return (
      <div className={styles.container}>
        <div className={`${styles.header} ${styles.headerSeparator}`}>
          <button className={styles.backButton} onClick={() => { if (!isManageAccessSaving) setManageAccessState(null); }} title={t('employeeDetail.cancel')}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <h1 className={styles.pageTitle}>
            {isViewOnly
              ? t('employeeDetail.viewAccessTitle', { name: displayName(user) })
              : t('employeeDetail.manageDeviceAccessTitle', { name: displayName(user) })}
          </h1>
        </div>
        <div className={styles.scrollArea}>
          <ManageDeviceAccessPage
            ref={manageRef}
            consumerAccountId={consumerAccountId}
            consumerUserId={id}
            locationId={manageAccessState.locationId}
            email={email}
            role={user.role}
            invitedUserId={user.user?.id ?? null}
            readOnly={isViewOnly}
            onSavingChange={setIsManageAccessSaving}
            onChangesCountChange={setManageAccessChangesCount}
            onClose={() => setManageAccessState(null)}
          />
        </div>
        {!isViewOnly && (
          <StickyFooter
            changesCount={manageAccessChangesCount}
            onCancel={() => setManageAccessState(null)}
            onSave={() => manageRef.current?.save()}
            isSaving={isManageAccessSaving}
          />
        )}
      </div>
    );
  }

  return (
    <div className={styles.container}>

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate('/employees')} title={t('employeeDetail.back')}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M12.5 15L7.5 10L12.5 5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <div className={styles.titleGroup}>
          <h1 className={styles.pageTitle}>{displayName(user)}</h1>
          {isActive && (
            <span className={styles.statusBadge}>
              {t('employeeDetail.active')}
            </span>
          )}
        </div>
        <div className={styles.headerActions}>
          {hasPermission(PermissionKey.USER_DELETE) && (
            <Button
              variant="destructiveTertiary"
              onClick={() => setShowDeleteModal(true)}
              className={`${styles.headerBtn} ${styles.deleteBtnBorder}`}
            >
              {t('employeeDetail.deleteUser')}
            </Button>
          )}
        </div>
      </div>

      {/* ── Tabs bar ────────────────────────────────────────────────── */}
      <div className={styles.tabsSection}>
        <div className={styles.tabs}>
          <div className={styles.tabItem}>
            <Button
              variant="text"
              className={`${styles.tab} ${activeTab === 'info' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('info')}
            >
              {t('employeeDetail.info')}
            </Button>
            <div className={`${styles.tabBar} ${activeTab === 'info' ? styles.tabBarActive : ''}`} />
          </div>
          <div className={styles.tabItem}>
            <Button
              variant="text"
              className={`${styles.tab} ${activeTab === 'activity' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('activity')}
            >
              {t('employeeDetail.activity')}
            </Button>
            <div className={`${styles.tabBar} ${activeTab === 'activity' ? styles.tabBarActive : ''}`} />
          </div>
          {isActive && (
            <div className={styles.tabItem}>
              <Button
                variant="text"
                className={`${styles.tab} ${activeTab === 'location' ? styles.tabActive : ''}`}
                onClick={() => setActiveTab('location')}
              >
                {t('employeeDetail.locations')}
              </Button>
              <div className={`${styles.tabBar} ${activeTab === 'location' ? styles.tabBarActive : ''}`} />
            </div>
          )}
        </div>
      </div>

      <div className={styles.body}>

        {deleteError && (
          <div className={styles.stateContainer}>
            <span className={styles.errorText}>{deleteError}</span>
          </div>
        )}

          {activeTab === 'info' && (
            <>
              <div className={styles.sectionHeader}>
                <p className={styles.sectionTitle}>{t('employeeDetail.userInformation')}</p>
                {hasPermission(PermissionKey.USER_INVITE) && (
                  <Button
                    variant="highlightTertiary"
                    onClick={() => setShowChangeRoleModal(true)}
                    disabled={!isActive || !user.user?.id || user.role === 'OWNER'}
                    className={`${styles.headerBtn} ${styles.changeRoleBtnBorder}`}
                  >
                    {t('employeeDetail.changeRole')}
                  </Button>
                )}
              </div>
              <div className={styles.tabContent}>
                <div className={styles.infoTableContainer}>
                  <table className={styles.infoTable}>
                    <colgroup>
                      <col style={{ width: '200px' }} />
                      <col style={{ width: '360px' }} />
                      <col />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>{t('employeeDetail.name')}</th>
                        <th>{t('employeeDetail.email')}</th>
                        <th>{t('employeeDetail.role')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{displayName(user)}</td>
                        <td>{email}</td>
                        <td>{user.role ? getRoleLabel(user.role, t) : '—'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
          {activeTab === 'activity' && (
            <div className={styles.tabContent}>
              <span>{t('employeeDetail.noActivity')}</span>
            </div>
          )}
          {activeTab === 'location' && isActive && (
            <EmployeeDetailLocationsTab
              consumerAccountId={consumerAccountId}
              consumerUserId={id}
              email={email}
              userName={displayName(user)}
              invitedUserId={user.user?.id ?? null}
              onManageAccess={handleManageAccess}
              onAddLocation={() => setShowAddLocation(true)}
              canManageUsers={hasPermission(PermissionKey.USER_INVITE)}
            />
          )}

      </div>

      {showDeleteModal && (
        <DeleteConfirmModal
          email={email}
          isActive={isActive}
          isDeleting={isDeleting}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}

      {showChangeRoleModal && consumerAccountId && id && user.user?.id && (
        <ChangeRoleModal
          currentRole={user.role}
          email={email}
          consumerAccountId={consumerAccountId}
          invitedUserId={user.user.id}
          consumerUserId={id}
          onClose={() => setShowChangeRoleModal(false)}
          onSuccess={handleRoleChanged}
        />
      )}

    </div>
  );
}
